/**
 * Add Admin Modal Component
 * Modal for adding new admin accounts
 */

import React, { useState } from 'react';
import { apiService } from '../../services/apiService';
import './AddAdminModal.css';

interface AddAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type AdminRole = 'finance' | 'support' | 'compliance' | 'content' | 'risk';

const AddAdminModal: React.FC<AddAdminModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AdminRole>('finance');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [createdAdmin, setCreatedAdmin] = useState<{ name: string; email: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!fullName || !email) {
      setError('Name and email are required');
      return;
    }

    setIsLoading(true);
    try {
      const result = await apiService.createAdmin({
        name: fullName,
        email,
        role,
      });
      
      // Store temporary password and admin info to display
      setTemporaryPassword(result.temporaryPassword || null);
      setCreatedAdmin({
        name: fullName,
        email: email,
      });
      
      // Don't close modal yet - show success with temporary password
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create admin account');
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(null);
      setFullName('');
      setEmail('');
      setRole('finance');
      setTemporaryPassword(null);
      setCreatedAdmin(null);
      onClose();
    }
  };

  const handleContinue = () => {
    // Reset form and close
    setFullName('');
    setEmail('');
    setRole('finance');
    setTemporaryPassword(null);
    setCreatedAdmin(null);
    onSuccess();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add New Admin</h2>
          <button className="modal-close" onClick={handleClose} disabled={isLoading}>
            ×
          </button>
        </div>

        {temporaryPassword && createdAdmin ? (
          <div className="add-admin-success">
            <div className="success-icon">✅</div>
            <h3>Admin Account Created Successfully!</h3>
            <p>The new admin has been created. They will need to complete their first login setup.</p>
            
            <div className="temporary-password-box">
              <label>Temporary Password</label>
              <div className="password-display">
                <code>{temporaryPassword}</code>
                <button
                  type="button"
                  className="copy-button"
                  onClick={() => {
                    navigator.clipboard.writeText(temporaryPassword);
                  }}
                  title="Copy to clipboard"
                >
                  📋
                </button>
              </div>
              <p className="password-note">
                This temporary password has been sent to <strong>{createdAdmin.email}</strong>.
                Share this password securely with the new admin.
              </p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="button-primary"
                onClick={handleContinue}
              >
                Continue
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="add-admin-form">
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="fullName">Full Name</label>
              <input
                type="text"
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter full name"
                disabled={isLoading}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                disabled={isLoading}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="role">Role</label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value as AdminRole)}
                disabled={isLoading}
                required
              >
                <option value="finance">Finance Admin</option>
                <option value="support">Support Admin</option>
                <option value="compliance">Compliance Admin</option>
                <option value="risk">Risk & AML Admin</option>
                <option value="content">Content Admin</option>
              </select>
            </div>

            <p className="info-note">
              A temporary password will be generated and sent to the admin's email.
              They will need to complete first login setup to set their password, PIN, and 2FA.
            </p>

            <div className="modal-actions">
              <button
                type="button"
                className="button-secondary"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button-primary"
                disabled={isLoading}
              >
                {isLoading ? 'Creating...' : 'Create Admin'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddAdminModal;

